import ConfirmDialog from "components/ConfirmDialog"
import ItemCover from "components/ItemCover"
import NoItems from "components/NoItems"
import PageLoader from "components/PageLoader"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import {
  BaseHeaderLayout,
  Box,
  ContentLayout,
  Flex,
  IconButton,
  LinkButton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  ToggleInput,
  Tr,
  Typography,
} from "@strapi/design-system"
import { Pencil, Plus, Trash } from "@strapi/icons"
import { useAlert } from "providers/AlertProvider"

export const LooksPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const service = getTintVTOService()
  const [looks, setLooks] = useState([])
  const [isLoading, setLoading] = useState(false)
  const [visibilityLoading, setVisibilityLoading] = useState(null)
  const [lookDelete, setLookDelete] = useState(null)
  const { showAlert } = useAlert()

  const updateLooks = async () => {
    setLoading(true)
    const { data } = await service.looks()

    setLooks(data?.allLooks || [])
    setLoading(false)
  }

  useEffect(() => {
    updateLooks()
  }, [])

  const toggleVisibility = async (target: any, index: number) => {
    setVisibilityLoading(index)
    await service.toggleLookVisibility(target.id, !target.visible)
    await updateLooks()
    showAlert({
      error: false,
      text: t("looksPage.visibilityUpdated", {
        name: looks[index].name,
        status: target.visible ? t("looksPage.hidden") : t("looksPage.visible"),
      }),
    })
    setVisibilityLoading(null)
  }

  return (
    <>
      <ConfirmDialog
        isOpen={!!lookDelete}
        onClose={() => setLookDelete(null)}
        onConfirm={async () => {
          await service.deleteLook(lookDelete?.id)
          setLookDelete(null)
          showAlert({
            error: false,
            text: t("looksPage.deleteSuccess", { name: lookDelete?.name }),
          })
          await updateLooks()
        }}
        text={t("looksPage.deleteConfirmation", { name: lookDelete?.name })}
      />
      <Box>
        <BaseHeaderLayout
          title={t("looksPage.title")}
          as={"h1"}
          primaryAction={
            <LinkButton startIcon={<Plus />} target="_self" href={`/looks/add`}>
              {t("looksPage.addLook")}
            </LinkButton>
          }
          subtitle={t("looksPage.subtitle", { count: looks.length })}
        />
        <ContentLayout>
          {!!looks.length && (
            <Table>
              <Thead>
                <Tr>
                  <Th>{t("looksPage.logo")}</Th>
                  <Th>{t("looksPage.name")}</Th>
                  <Th>{t("looksPage.products")}</Th>
                  <Th></Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {looks.map((look: any, index: number) => (
                  <Tr key={index}>
                    <Td>
                      <Box padding={1}>
                        <ItemCover cover={look.cover} name={look.name} size={64} />
                      </Box>
                    </Td>
                    <Td>
                      <Typography textColor="neutral800">{look.name}</Typography>
                    </Td>
                    <Td>
                      {t("looksPage.productsCount", { count: look.products.selectedSkus.length })}
                    </Td>
                    <Td>
                      <ToggleInput
                        disabled={visibilityLoading === index}
                        checked={look.visible}
                        onChange={() => toggleVisibility(look, index)}
                        onLabel={t("looksPage.visible")}
                        offLabel={t("looksPage.hidden")}
                      />
                    </Td>
                    <Td>
                      <Flex gap={2}>
                        <IconButton
                          onClick={() => setLookDelete(look)}
                          label={t("looksPage.delete")}
                          icon={<Trash />}
                        />
                        <IconButton
                          onClick={() => navigate(`/looks/edit?id=${look.id}`)}
                          label={t("looksPage.edit")}
                          icon={<Pencil />}
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
          {isLoading && <PageLoader />}
          {!isLoading && !looks.length && (
            <NoItems
              text={t("looksPage.noLooks")}
              addText={t("looksPage.addFirstLook")}
              addLink={`/looks/add`}
              showAdd
            />
          )}
        </ContentLayout>
      </Box>
    </>
  )
}
